import type { ReactNode } from 'react';
import { Badge } from '@/components/ui';
import type { Vitals } from '@/features/queue/types';
import styles from './queue.module.css';

const fmt = (v: number | null, unit = '') => (v === null ? '—' : `${v}${unit}`);

/** Triage vitals as the doctor sees them in the consultation. */
export function VitalsSummary({
  vitals,
  action,
}: {
  vitals: Vitals | null;
  /** e.g. "Retake" in the consultation. */
  action?: ReactNode;
}) {
  if (!vitals)
    return (
      <p className={styles.flagLine}>
        <span className={styles.sub}>No vitals taken yet for this visit.</span>
        {action}
      </p>
    );
  const time = new Date(vitals.recordedAt).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <div className={styles.vitalsCard}>
      <dl className={styles.vitalsRow}>
        <div>
          <dt>BP</dt>
          <dd>
            {vitals.bpSystolic !== null || vitals.bpDiastolic !== null
              ? `${fmt(vitals.bpSystolic)}/${fmt(vitals.bpDiastolic)}`
              : '—'}
          </dd>
        </div>
        <div>
          <dt>Pulse</dt>
          <dd>{fmt(vitals.pulse, '/min')}</dd>
        </div>
        <div>
          <dt>Temp</dt>
          <dd>{fmt(vitals.temperatureC, ' °C')}</dd>
        </div>
        <div>
          <dt>SpO2</dt>
          <dd>{fmt(vitals.spo2, ' %')}</dd>
        </div>
        <div>
          <dt>Weight</dt>
          <dd>{fmt(vitals.weightKg, ' kg')}</dd>
        </div>
        <div>
          <dt>BMI</dt>
          <dd>{fmt(vitals.bmi)}</dd>
        </div>
        <div>
          <dt>Glucose</dt>
          <dd>{fmt(vitals.glucoseMmol, ' mmol/L')}</dd>
        </div>
      </dl>
      <p className={styles.flagLine}>
        {vitals.flags.length ? (
          vitals.flags.map((f) => (
            <Badge key={f.label} tone="danger">
              {f.label}
            </Badge>
          ))
        ) : (
          <Badge tone="success">All within range</Badge>
        )}
        <span className={styles.sub}>
          Taken {time}
          {vitals.recordedBy ? ` by ${vitals.recordedBy.fullName}` : ''}
        </span>
        {action}
      </p>
    </div>
  );
}
